/**
 * Edge-refraction optics adapted from deepika-builds/liquid-glass.
 * https://github.com/deepika-builds/liquid-glass
 *
 * MIT License
 *
 * Copyright (c) 2026 Deepika Rao
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";
const RESIZE_DEBOUNCE_MS = 120;

let nextFilterId = 0;

export interface LiquidGlassOptions {
  /** Displacement strength; negative values produce a magnifying rim. */
  scale?: number;
  /** Per-channel scale stagger. Set to zero to disable the prism fringe. */
  chroma?: number;
  /** Neutral interior inset as a fraction of the shorter side. */
  border?: number;
  /** Curvature softness for the edge displacement map, in pixels. */
  mapBlur?: number;
  /** Backdrop blur inside supported refracting glass, in pixels. */
  blur?: number;
  /** Backdrop saturation multiplier. */
  saturate?: number;
  /** Optional radius override. The computed border radius is used otherwise. */
  radius?: number | null;
  /** Frosted fallback blur, in pixels. */
  fallbackBlur?: number;
}

export interface LiquidGlassEffect {
  readonly supported: boolean;
  refresh: () => void;
  destroy: () => void;
}

interface SupportSignals {
  userAgent: string;
  supportsBackdropFilter: boolean;
  canReadCanvas: boolean;
}

interface CssSupportsApi {
  supports?: (property: string, value: string) => boolean;
}

interface LiquidGlassEnginePlatform {
  document: Document;
  getComputedStyle: typeof getComputedStyle;
  ResizeObserver?: typeof ResizeObserver;
  supported?: boolean;
}

export interface LiquidGlassEngine {
  readonly supported: boolean;
  apply: (
    element: HTMLElement,
    options?: LiquidGlassOptions,
  ) => LiquidGlassEffect;
  destroy: () => void;
}

const DEFAULT_OPTIONS = {
  scale: -112,
  chroma: 6,
  border: 0.07,
  mapBlur: 12,
  blur: 3,
  saturate: 1.5,
  radius: null,
  fallbackBlur: 16,
} satisfies Required<LiquidGlassOptions>;

export function detectLiquidGlassSupport({
  userAgent,
  supportsBackdropFilter,
  canReadCanvas,
}: SupportSignals): boolean {
  const isSafari =
    /Safari/.test(userAgent) && !/Chrome|Chromium|Edg/.test(userAgent);
  const isFirefox = /Firefox/.test(userAgent);

  return !isSafari && !isFirefox && supportsBackdropFilter && canReadCanvas;
}

function detectBrowserSupport(document: Document): boolean {
  const view = document.defaultView;
  if (!view || typeof view.ResizeObserver !== "function") return false;

  const css = Reflect.get(view, "CSS") as CssSupportsApi | undefined;
  const supportsBackdropFilter =
    typeof css?.supports === "function" &&
    css.supports("backdrop-filter", "url(#liquid-glass)");

  let canReadCanvas = false;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 4;
    canvas.height = 4;
    const context = canvas.getContext("2d");
    context?.getImageData(0, 0, 1, 1);
    canReadCanvas = context !== null;
  } catch {
    canReadCanvas = false;
  }

  return detectLiquidGlassSupport({
    canReadCanvas,
    supportsBackdropFilter,
    userAgent: view.navigator.userAgent,
  });
}

function createSvgElement<K extends keyof SVGElementTagNameMap>(
  document: Document,
  name: K,
): SVGElementTagNameMap[K] {
  return document.createElementNS(SVG_NAMESPACE, name);
}

function createDisplacementMap(
  document: Document,
  width: number,
  height: number,
  radius: number,
  border: number,
  mapBlur: number,
): string {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Liquid glass requires a 2D canvas context");

  const horizontal = context.createLinearGradient(0, 0, width, 0);
  horizontal.addColorStop(0, "rgb(0,0,0)");
  horizontal.addColorStop(1, "rgb(255,0,0)");
  context.fillStyle = horizontal;
  context.fillRect(0, 0, width, height);

  const vertical = context.createLinearGradient(0, 0, 0, height);
  vertical.addColorStop(0, "rgb(0,0,0)");
  vertical.addColorStop(1, "rgb(0,0,255)");
  context.globalCompositeOperation = "difference";
  context.fillStyle = vertical;
  context.fillRect(0, 0, width, height);
  context.globalCompositeOperation = "source-over";

  const inset = border * Math.min(width, height);
  context.filter = `blur(${String(mapBlur)}px)`;
  context.fillStyle = "rgba(128,128,128,0.93)";
  context.beginPath();
  context.roundRect(
    inset,
    inset,
    Math.max(1, width - inset * 2),
    Math.max(1, height - inset * 2),
    Math.max(radius - inset, 2),
  );
  context.fill();
  context.filter = "none";

  return canvas.toDataURL();
}

function resolveRadius(
  element: HTMLElement,
  width: number,
  height: number,
  override: number | null,
  readStyle: typeof getComputedStyle,
): number {
  if (override !== null) return override;

  const rawRadius = readStyle(element).borderTopLeftRadius || "0px";
  const value = Number.parseFloat(rawRadius) || 0;
  return rawRadius.trim().endsWith("%")
    ? (value / 100) * Math.min(width, height)
    : value;
}

function captureStyle(element: HTMLElement, property: string) {
  return {
    priority: element.style.getPropertyPriority(property),
    value: element.style.getPropertyValue(property),
  };
}

function restoreStyle(
  element: HTMLElement,
  property: string,
  previous: { priority: string; value: string },
) {
  if (previous.value) {
    element.style.setProperty(property, previous.value, previous.priority);
  } else {
    element.style.removeProperty(property);
  }
}

export function createLiquidGlassEngine(
  platform?: LiquidGlassEnginePlatform,
): LiquidGlassEngine {
  if (!platform && typeof document === "undefined") {
    throw new Error("Liquid glass can only be initialized in a browser");
  }

  const browserDocument = platform?.document ?? document;
  const readStyle = platform?.getComputedStyle ?? getComputedStyle;
  const Observer =
    platform?.ResizeObserver ?? browserDocument.defaultView?.ResizeObserver;
  const supported =
    platform?.supported ?? detectBrowserSupport(browserDocument);
  const activeEffects = new Set<LiquidGlassEffect>();
  let svgHost: SVGSVGElement | null = null;
  let svgDefinitions: SVGDefsElement | null = null;

  const ensureDefinitions = () => {
    if (svgDefinitions) return svgDefinitions;

    svgHost = createSvgElement(browserDocument, "svg");
    svgHost.setAttribute("width", "0");
    svgHost.setAttribute("height", "0");
    svgHost.setAttribute("aria-hidden", "true");
    svgHost.dataset.liquidGlassDefinitions = "";
    svgHost.style.position = "absolute";
    svgHost.style.pointerEvents = "none";

    svgDefinitions = createSvgElement(browserDocument, "defs");
    svgHost.append(svgDefinitions);
    browserDocument.body.append(svgHost);
    return svgDefinitions;
  };

  const buildFilter = (id: string, scales: readonly number[]) => {
    const filter = createSvgElement(browserDocument, "filter");
    filter.id = id;
    filter.setAttribute("x", "0");
    filter.setAttribute("y", "0");
    filter.setAttribute("width", "100%");
    filter.setAttribute("height", "100%");
    filter.setAttribute("color-interpolation-filters", "sRGB");

    const image = createSvgElement(browserDocument, "feImage");
    image.setAttribute("x", "0");
    image.setAttribute("y", "0");
    image.setAttribute("result", "map");
    image.setAttribute("preserveAspectRatio", "none");
    filter.append(image);

    const channelMatrices = [
      "1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0",
      "0 0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 1 0",
      "0 0 0 0 0 0 0 0 0 0 0 0 1 0 0 0 0 0 1 0",
    ];

    for (const [index, scale] of scales.entries()) {
      const displacement = createSvgElement(
        browserDocument,
        "feDisplacementMap",
      );
      displacement.setAttribute("in", "SourceGraphic");
      displacement.setAttribute("in2", "map");
      displacement.setAttribute("scale", String(scale));
      displacement.setAttribute("xChannelSelector", "R");
      displacement.setAttribute("yChannelSelector", "B");
      displacement.setAttribute("result", `displaced-${String(index)}`);
      filter.append(displacement);

      const colorMatrix = createSvgElement(browserDocument, "feColorMatrix");
      colorMatrix.setAttribute("in", `displaced-${String(index)}`);
      colorMatrix.setAttribute("type", "matrix");
      colorMatrix.setAttribute("values", channelMatrices[index] ?? "");
      colorMatrix.setAttribute("result", `channel-${String(index)}`);
      filter.append(colorMatrix);
    }

    const firstBlend = createSvgElement(browserDocument, "feBlend");
    firstBlend.setAttribute("in", "channel-0");
    firstBlend.setAttribute("in2", "channel-1");
    firstBlend.setAttribute("mode", "screen");
    firstBlend.setAttribute("result", "channels-01");
    filter.append(firstBlend);

    const secondBlend = createSvgElement(browserDocument, "feBlend");
    secondBlend.setAttribute("in", "channels-01");
    secondBlend.setAttribute("in2", "channel-2");
    secondBlend.setAttribute("mode", "screen");
    filter.append(secondBlend);

    ensureDefinitions().append(filter);
    return { filter, image };
  };

  const apply = (
    element: HTMLElement,
    options: LiquidGlassOptions = {},
  ): LiquidGlassEffect => {
    const resolved = { ...DEFAULT_OPTIONS, ...options };
    const previousBackdrop = captureStyle(element, "backdrop-filter");
    const previousWebkitBackdrop = captureStyle(
      element,
      "-webkit-backdrop-filter",
    );
    const previousRuntime = element.dataset.liquidGlassRuntime;
    let destroyed = false;

    if (!supported) {
      const frosted = `blur(${String(resolved.fallbackBlur)}px) saturate(${String(resolved.saturate)})`;
      element.style.setProperty("backdrop-filter", frosted);
      element.style.setProperty("-webkit-backdrop-filter", frosted);
      element.dataset.liquidGlassRuntime = "fallback";

      const effect: LiquidGlassEffect = {
        supported: false,
        refresh: () => {},
        destroy: () => {
          if (destroyed) return;
          destroyed = true;
          restoreStyle(element, "backdrop-filter", previousBackdrop);
          restoreStyle(
            element,
            "-webkit-backdrop-filter",
            previousWebkitBackdrop,
          );
          if (previousRuntime === undefined) {
            delete element.dataset.liquidGlassRuntime;
          } else {
            element.dataset.liquidGlassRuntime = previousRuntime;
          }
          activeEffects.delete(effect);
        },
      };
      activeEffects.add(effect);
      return effect;
    }

    if (!Observer) {
      throw new Error("Liquid glass refraction requires ResizeObserver");
    }

    const filterId = `atriam-liquid-glass-${String(++nextFilterId)}`;
    const scales = [
      resolved.scale,
      resolved.scale + resolved.chroma,
      resolved.scale + resolved.chroma * 2,
    ];
    const { filter, image } = buildFilter(filterId, scales);
    let mapGeometry:
      | { width: number; height: number; radius: number }
      | undefined;

    const refresh = () => {
      const width = Math.round(element.offsetWidth);
      const height = Math.round(element.offsetHeight);
      if (width <= 0 || height <= 0) return;

      const radius = resolveRadius(
        element,
        width,
        height,
        resolved.radius,
        readStyle,
      );
      // Map and editor DOM updates also refresh glass; reuse its static texture.
      if (
        mapGeometry?.width === width &&
        mapGeometry.height === height &&
        mapGeometry.radius === radius
      ) {
        return;
      }
      image.setAttribute(
        "href",
        createDisplacementMap(
          browserDocument,
          width,
          height,
          radius,
          resolved.border,
          resolved.mapBlur,
        ),
      );
      image.setAttribute("width", String(width));
      image.setAttribute("height", String(height));
      mapGeometry = { width, height, radius };
    };

    refresh();
    element.style.setProperty(
      "backdrop-filter",
      `url(#${filterId}) blur(${String(resolved.blur)}px) saturate(${String(resolved.saturate)})`,
    );
    element.style.setProperty(
      "-webkit-backdrop-filter",
      `url(#${filterId}) blur(${String(resolved.blur)}px) saturate(${String(resolved.saturate)})`,
    );
    element.dataset.liquidGlassRuntime = "active";

    let resizeTimer: ReturnType<typeof setTimeout> | undefined;
    const resizeObserver = new Observer(() => {
      if (resizeTimer !== undefined) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(refresh, RESIZE_DEBOUNCE_MS);
    });
    resizeObserver.observe(element);

    const effect: LiquidGlassEffect = {
      supported: true,
      refresh,
      destroy: () => {
        if (destroyed) return;
        destroyed = true;
        resizeObserver.disconnect();
        if (resizeTimer !== undefined) clearTimeout(resizeTimer);
        filter.remove();
        restoreStyle(element, "backdrop-filter", previousBackdrop);
        restoreStyle(
          element,
          "-webkit-backdrop-filter",
          previousWebkitBackdrop,
        );
        if (previousRuntime === undefined) {
          delete element.dataset.liquidGlassRuntime;
        } else {
          element.dataset.liquidGlassRuntime = previousRuntime;
        }
        activeEffects.delete(effect);
      },
    };
    activeEffects.add(effect);
    return effect;
  };

  return {
    supported,
    apply,
    destroy: () => {
      for (const effect of [...activeEffects]) effect.destroy();
      svgHost?.remove();
      svgHost = null;
      svgDefinitions = null;
    },
  };
}
